/**
 * Transcripts.
 *
 * Two panes rather than a list that navigates away: reading conversations is a
 * scanning task, and losing your place in the list after every one makes forty
 * of them unreadable.
 */

import { useState } from "react";

import { useConversation, useConversations } from "../../api/queries";
import type { Conversation, Message } from "../../api/types";
import {
  Badge,
  Card,
  Empty,
  ErrorNote,
  Loading,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

export function ConversationsPage() {
  const { orgId, siteId } = useSiteContext();
  const conversations = useConversations(orgId, siteId);
  const [selected, setSelected] = useState<string | null>(null);

  if (conversations.isLoading) return <Loading />;
  if (conversations.error) return <ErrorNote error={conversations.error} />;

  const rows = conversations.data ?? [];
  if (rows.length === 0) {
    return (
      <Empty title="No conversations yet">
        They appear here as soon as a visitor uses the widget on your store.
      </Empty>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
      <Card className="max-h-[36rem] overflow-y-auto" title={`${rows.length} conversations`}>
        <ul className="divide-y divide-line">
          {rows.map((conversation) => (
            <li key={conversation.id}>
              <ConversationButton
                conversation={conversation}
                selected={conversation.id === selected}
                onSelect={() => setSelected(conversation.id)}
              />
            </li>
          ))}
        </ul>
      </Card>

      {selected ? (
        <Transcript orgId={orgId} siteId={siteId} conversationId={selected} />
      ) : (
        <Empty title="Select a conversation">Its full transcript appears here.</Empty>
      )}
    </div>
  );
}

function ConversationButton({
  conversation,
  selected,
  onSelect,
}: {
  conversation: Conversation;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      // `aria-current` rather than colour alone: which row is open has to be
      // conveyed to a screen reader too.
      aria-current={selected ? "true" : undefined}
      className={`w-full px-1 py-2 text-left ${selected ? "font-medium" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">
          {new Date(conversation.created_at).toLocaleString()}
        </span>
        <Badge>{conversation.message_count}</Badge>
      </div>
      {conversation.page_url && (
        <div className="truncate text-xs text-muted">{conversation.page_url}</div>
      )}
    </button>
  );
}

function Transcript({
  orgId,
  siteId,
  conversationId,
}: {
  orgId: string;
  siteId: string;
  conversationId: string;
}) {
  const conversation = useConversation(orgId, siteId, conversationId);

  if (conversation.isLoading) return <Loading />;
  if (conversation.error) return <ErrorNote error={conversation.error} />;
  if (!conversation.data) return null;

  return (
    <Card
      title="Transcript"
      action={
        <span className="text-xs text-muted">
          {conversation.data.locale ?? "unknown locale"}
        </span>
      }
    >
      <ol className="space-y-3">
        {conversation.data.messages.map((message) => (
          <li key={message.id}>
            <Bubble message={message} />
          </li>
        ))}
      </ol>
    </Card>
  );
}

function Bubble({ message }: { message: Message }) {
  const isUser = message.role === "user";

  return (
    <div className={isUser ? "text-right" : ""}>
      <div
        className={`inline-block max-w-[85%] rounded-xl px-3 py-2 text-left text-sm ${
          isUser ? "bg-brand text-white" : "bg-surface"
        }`}
      >
        {message.content || <em className="text-muted">(no text)</em>}
      </div>

      {message.error && (
        <p className="mt-1 text-xs text-bad">{message.error}</p>
      )}

      {/*
        What the answer was based on. This is the row that answers "why did it
        say that?", and it is drawn from stored citations rather than from the
        model's prose — so it is what was actually retrieved.
      */}
      {message.citations.length > 0 && (
        <p className="mt-1 text-xs text-muted">
          Based on:{" "}
          {message.citations
            .map((citation) => String(citation.title ?? citation.document_id))
            .join(", ")}
        </p>
      )}
    </div>
  );
}
